import {
  type City,
  type CityArea,
  type DoctorsListOrder,
  type ServiceDetails,
} from "@hermes/api";
import { Message, Temporal, useMessage } from "@hermes/intl";
import type { GeoPosition } from "@hermes/shared";
import {
  cssFns,
  icons,
  useColors,
  usePrincipalColors,
  useScreenDetector,
} from "@hermes/ui";
import { Fragment } from "react";

import { useAppConfig } from "#internal/app/config";

import { DoctorCard, type DoctorCardService } from "../doctor-card";
import { DoctorsListLoading } from "./loading";
import { useCombinedDoctorsPages } from "./request-blocks/use-combined-doctor-pages";

const InfoBox = ({ title, subtitle }: { title: string; subtitle: string }) => {
  const { principal } = useColors();
  // const { isMobile } = useScreenDetector();
  return (
    <div
      css={[
        cssFns.padding(10),
        cssFns.border({
          width: 1,
          style: "solid",
          color: principal.ebblue,
          radius: 12,
        }),
        {
          // marginBlockEnd: isMobile ? 16 : 20,
          // marginBlockStart: isMobile ? -16 : -20,
          background: principal.grayline,
        },
      ]}
    >
      <div
        css={{
          display: "flex",
          gap: 8,
        }}
      >
        <div css={[cssFns.padding(3)]}>
          <icons.InfoIcon />
        </div>
        <p
          css={[
            cssFns.typo({ weight: "semibold", level: "body-1" }),
            { marginBlockStart: 2 },
          ]}
        >
          {title}
        </p>
      </div>
      <div css={{ marginInlineStart: 30 }}>{subtitle}</div>
    </div>
  );
};

export const DoctorsList = ({
  sort,
  targetServices,
  position,
  city,
  cityArea,
  clinicId,
  isBordered,
  displayCount,
  slotDate,
  enablePromo,
  pageHeader,
  language,
  insurance,
  currentService,
  displayAllReviews,
}: {
  sort?: DoctorsListOrder;
  targetServices?: DoctorCardService[];
  position?: GeoPosition;
  city: City;
  cityArea?: CityArea;
  clinicId?: number;
  isBordered?: boolean;
  displayCount?: boolean;
  slotDate?: Temporal.PlainDate;
  enablePromo?: boolean;
  pageHeader: string;
  language?: string;
  insurance?: number;
  currentService?: ServiceDetails;
  displayAllReviews?: boolean;
  shownDoctorIds?: number[];
}) => {
  const { isMobile } = useScreenDetector();
  const principalColors = usePrincipalColors();
  const { insurances } = useAppConfig();
  const message = useMessage();

  const { pages, count, limit, premiumCount, fetchPage, fallbackStartIndex } =
    useCombinedDoctorsPages({
      sort,
      targetServices,
      city,
      cityArea,
      clinicId,
      position,
      slotDate,
      enablePromo,
      language,
      insurance,
      currentService,
    });

  const FETCH_ON_INDEX = isMobile ? 3 : 6;

  const shouldShowFilterNotice = Boolean(insurance || cityArea);
  const filterTitle =
    insurance && cityArea
      ? message({
          id: "61a8f9d0-98a0-4941-b1fa-c0bd356a9abe",
          default: "No more doctors left in {city_area}, {city}, {insurance}",
          values: {
            city_area: cityArea.name,
            city: city.name,
            insurance: insurances.find(({ id }) => id === Number(insurance))
              ?.name,
          },
        })
      : insurance
        ? message({
            id: "42764684-131a-44b2-8906-a41a4f85a98b",
            default: "No more doctors left {name}",
            values: {
              name: insurances.find(({ id }) => id === Number(insurance))?.name,
            },
          })
        : cityArea
          ? message({
              id: "61d9fe86-940d-49c3-b88a-22f64be8febf",
              default: "No more doctors left in {city_area}, {city}",
              values: {
                city_area: cityArea.name,
                city: city.name,
              },
            })
          : "";

  const filterSubtitle = cityArea
    ? message({
        id: "c7af8b3d-b7de-44d7-a79f-014b56db9d48",
        default: "Check out other doctors in nearby areas below",
      })
    : message({
        id: "f4d6ee4f-4cc5-4d04-9dd2-87b17d907a85",
        default: "Посмотрите ниже других врачей",
      });

  return (
    <div>
      {displayCount && (
        <div css={{ margin: isMobile ? "0 0 10px" : "0 0 20px" }}>
          <h2
            css={[
              { margin: 0, color: principalColors.gs4 },
              cssFns.typo({ level: "body-1", weight: "regular" }),
            ]}
          >
            <Message
              id="2c5660dc-ff4f-4326-a6ba-93d3dba66f13"
              default="Найдено врачей: {count}"
              values={{ count }}
            />
          </h2>
        </div>
      )}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          rowGap: 16,
        }}
      >
        {pages.map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            {pageIndex === fallbackStartIndex &&
              shouldShowFilterNotice &&
              pages.length > fallbackStartIndex &&
              pages.slice(fallbackStartIndex).some((p) => p.cards?.length) && (
                <InfoBox title={filterTitle} subtitle={filterSubtitle} />
              )}
            {!page.seen || page.isFetching ? (
              <DoctorsListLoading
                limit={page.size}
                isBordered
                onInView={() => fetchPage(pageIndex)}
              />
            ) : (
              page.cards?.map(
                (
                  {
                    doctor,
                    branches_services,
                    initialSlots,
                    branches_specialties,
                  },
                  index,
                ) => {
                  const list_position = doctor.premium
                    ? 0
                    : pageIndex * limit + index + 1 - premiumCount;
                  const shouldTriggerFetch =
                    pageIndex === 0
                      ? index + 1 - premiumCount >= FETCH_ON_INDEX
                      : index + 1 >= FETCH_ON_INDEX;

                  return (
                    <DoctorCard
                      key={`${doctor.id}-${doctor.premium}`}
                      defaultStartDate={slotDate}
                      doctor={doctor}
                      city={city}
                      cityArea={cityArea}
                      branches_services={branches_services}
                      branches_specialties={branches_specialties}
                      initialSlots={initialSlots}
                      isBordered={isBordered}
                      request_meta={{
                        list_position: Math.max(list_position, 0),
                        booking_page: pageHeader,
                      }}
                      areSlotsEmpty={page.areSlotsEmpty}
                      areServicesLoading={page.areServicesFetching}
                      areBranchesLoading={page.areBranchesFetching}
                      areSlotsLoading={page.areSlotsFetching}
                      heading={!!clinicId ? "h3" : undefined}
                      noRedirectToClinic={!!clinicId}
                      premium={doctor.premium}
                      onInView={() => {
                        if (shouldTriggerFetch) {
                          fetchPage(pageIndex + 1);
                        }
                      }}
                      pageHeader={pageHeader}
                      displayAllReviews={displayAllReviews}
                    />
                  );
                },
              )
            )}
          </Fragment>
        ))}
      </div>

      {count === undefined && <DoctorsListLoading limit={limit} isBordered />}

      {count === 0 && (
        <div
          css={[
            cssFns.typo({ level: "title-4" }),
            { textAlign: "center", marginBlockEnd: 200 },
          ]}
        >
          <Message
            id="3b187e89-5f24-429f-a31d-4991958c3fb0"
            default="No doctors were found for your query."
          />
        </div>
      )}
    </div>
  );
};
